/**
 * AI Studio — Agent Factory view models (S1–S5).
 *
 * These mirror the backend `ai_studio_agents` / `ai_studio_agent_versions` /
 * `ai_studio_prompt_bindings` / `ai_studio_industry_packs` /
 * `ai_studio_agent_bindings` rows in camelCase.
 * The service module owns the snake_case boundary so persistence column names
 * never leak into components.
 */

export type StudioLifecycleStatus =
  | 'draft'
  | 'published'
  | 'superseded'
  | 'deprecated'
  | 'archived';

/** Agent catalog availability — versions may also be `superseded`. */
export type StudioAgentLifecycleStatus = Exclude<StudioLifecycleStatus, 'superseded'>;

/** Matches the ai_studio_agent_versions.execution_mode CHECK constraint. */
export type StudioExecutionMode = 'propose_approve' | 'autonomous_override';

/**
 * Decision OS claim mode for Decision agents (definition dropdown).
 * Explore was retired — Provisional covers analysis with or without saved outputs.
 */
export type StudioDecisionMode = 'PROVISIONAL' | 'VERIFIED' | 'EVIDENCE_ONLY';

export const STUDIO_DECISION_MODES: readonly StudioDecisionMode[] = [
  'PROVISIONAL',
  'VERIFIED',
  'EVIDENCE_ONLY',
];

/** The three ways an agent may compute (Design §13). Internal code values; UI labels differ. */
export type StudioComputeClass = 'deterministic' | 'learned' | 'generative';

/** Which compute class carries the agent's core value. */
export type StudioAgentArchetype =
  | 'engine_centric'
  | 'predictor_centric'
  | 'perception_centric'
  | 'language_centric';

/** Fallback policy for a compute slot when it cannot be resolved at run time. */
export type StudioSlotFallbackPolicy = 'error' | 'industry_average' | 'skip';

/** Shared capability spine: Ground → Analyze → Generate/Act → Explain → Remember → Promote. */
export type StudioSpineStage =
  | 'ground'
  | 'analyze'
  | 'generate_act'
  | 'explain'
  | 'remember'
  | 'promote';

export const STUDIO_LIFECYCLE_STATUSES: readonly StudioLifecycleStatus[] = [
  'draft',
  'published',
  'superseded',
  'deprecated',
  'archived',
];

export const STUDIO_AGENT_LIFECYCLE_STATUSES: readonly StudioAgentLifecycleStatus[] = [
  'draft',
  'published',
  'archived',
  'deprecated',
];

export const STUDIO_EXECUTION_MODES: readonly StudioExecutionMode[] = [
  'propose_approve',
  'autonomous_override',
];

export const STUDIO_COMPUTE_CLASSES: readonly StudioComputeClass[] = [
  'deterministic',
  'learned',
  'generative',
];

export const STUDIO_AGENT_ARCHETYPES: readonly StudioAgentArchetype[] = [
  'engine_centric',
  'predictor_centric',
  'perception_centric',
  'language_centric',
];

export const STUDIO_SLOT_FALLBACK_POLICIES: readonly StudioSlotFallbackPolicy[] = [
  'error',
  'industry_average',
  'skip',
];

/**
 * Maps each archetype to its dominant compute class.
 * Used to filter available archetype options to only those whose class is declared.
 */
export const ARCHETYPE_CLASS_MAP: Record<StudioAgentArchetype, StudioComputeClass> = {
  engine_centric:     'deterministic',
  predictor_centric:  'learned',
  perception_centric: 'learned',
  language_centric:   'generative',
};

export const STUDIO_SPINE_STAGES: readonly StudioSpineStage[] = [
  'ground',
  'analyze',
  'generate_act',
  'explain',
  'remember',
  'promote',
];

/** Agent visibility for catalog / sharing (distinct from lifecycle_status). */
export type StudioAgentVisibility = 'draft' | 'private' | 'public' | 'archived';

/** Unified agent state (single field — Design DR1). */
export type StudioAgentState = StudioAgentVisibility;

export const STUDIO_AGENT_VISIBILITIES: readonly StudioAgentVisibility[] = [
  'draft',
  'private',
  'public',
  'archived',
];

/** States editable on the definition page while still Draft (archive via catalog or after release). */
export const STUDIO_AGENT_DEFINITION_STATES: readonly StudioAgentState[] = [
  'draft',
  'private',
  'public',
];

/** States allowed once the agent has left Draft (definition page State select). */
export const STUDIO_AGENT_LOCKED_STATES: readonly StudioAgentState[] = [
  'private',
  'public',
  'archived',
];

export const STUDIO_AGENT_STATES: readonly StudioAgentState[] = STUDIO_AGENT_VISIBILITIES;

/** Structured I/O row for thick authoring (DR1 B2/B4). */
export interface ExpectedFieldRow {
  /** Stable id in customer_setup.fields — auto-slugged from label when omitted. */
  key?: string;
  label: string;
  /**
   * Inputs: what it is + typical source.
   * Outputs: what it is + delivery format (PDF, Excel, …).
   */
  description: string;
  required?: boolean;
  /** Setup control type — inferred from label/description when omitted. */
  type?: SetupFieldType;
  /** When to collect: provision (instance setup) vs run (conversation). */
  collectAt?: SetupCollectAt;
  enumValues?: string[];
}

export const SETUP_FIELD_TYPES = [
  'text',
  'textarea',
  'secret',
  'number',
  'boolean',
  'enum',
  'json',
  'file',
] as const;

export type SetupFieldType = (typeof SETUP_FIELD_TYPES)[number];

export const SETUP_COLLECT_AT = ['provision', 'run', 'optional'] as const;

export type SetupCollectAt = (typeof SETUP_COLLECT_AT)[number];

export interface SetupFieldSchema {
  key: string;
  label: string;
  description: string;
  type: SetupFieldType;
  collectAt: SetupCollectAt;
  required: boolean;
  enumValues?: string[];
  source: 'platform' | 'agent' | 'pack';
}

export interface InstanceSetupSchema {
  instanceId: string;
  agentId: string;
  agentCode: string;
  agentVersionId: string;
  versionNumber: number;
  industryPackId: string | null;
  fields: SetupFieldSchema[];
  provisionFields: SetupFieldSchema[];
}

/** Pack resource type seed for scheduling ontology (DR2). */
export interface ResourceTypeSeed {
  typeCode: string;
  displayName: string;
  attributes: {
    attributeCode: string;
    dataType: string;
    isRequired?: boolean;
  }[];
}

export interface AiStudioAgent {
  id: string;
  /** Null for platform-owned catalog agents; set for tenant-owned agents. */
  tenantId: string | null;
  /** Display name for tenant-owned agents (joined on list). */
  tenantName?: string | null;
  code: string;
  name: string;
  description: string | null;
  domain: string | null;
  industryTags: string[];
  isPlatform: boolean;
  purpose: string | null;
  scopeHonesty: string | null;
  expertPosture: string | null;
  visibility: StudioAgentVisibility;
  lifecycleStatus: StudioAgentLifecycleStatus;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A version is the agent's blueprint at a point in time. The four JSON documents
 * (`dataSchema`, `outputSchema`, `engineConfig`, …) stay opaque in the foundation UI:
 * they are edited as JSON and validated only for well-formedness.
 */
export interface AiStudioAgentVersion {
  id: string;
  agentId: string;
  versionNumber: number;
  enabledStages: StudioSpineStage[];
  dataSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  benchmarkConfig: Record<string, unknown>;
  opportunityLogic: Record<string, unknown>;
  engineConfig: Record<string, unknown>;
  promotionRules: Record<string, unknown>;
  executionMode: StudioExecutionMode;
  autonomyFences: Record<string, unknown>;
  /** Author-selected Decision mode default for runs of this version. */
  defaultDecisionMode: StudioDecisionMode;
  contextContract: Record<string, unknown>;
  computeClasses: StudioComputeClass[];
  archetype: string | null;
  industryPackId: string | null;
  inputExpected: ExpectedFieldRow[];
  outputExpected: ExpectedFieldRow[];
  contextDefinition: string | null;
  explainInstructions: string | null;
  successCriteria: string | null;
  hitlRequired: boolean;
  lifecycleStatus: StudioLifecycleStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiStudioPromptBinding {
  id: string;
  agentVersionId: string;
  promptTemplateId: string | null;
  promptRole: string;
  taskType: string;
  variableBindings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AiStudioIndustryPack {
  id: string;
  /** Null for platform seed packs; set for tenant-owned packs. */
  tenantId: string | null;
  code: string;
  name: string;
  domain: string | null;
  vocabularyMap: Record<string, unknown>;
  enabledPlugins: unknown[];
  defaultWeights: Record<string, unknown>;
  importTemplate: Record<string, unknown>;
  description: string | null;
  purpose: string | null;
  scopeHonesty: string | null;
  expertPosture: string | null;
  appendInputExpected: ExpectedFieldRow[];
  appendOutputExpected: ExpectedFieldRow[];
  appendContext: string | null;
  appendInstructions: string | null;
  resourceTypes: ResourceTypeSeed[];
  lifecycleStatus: StudioLifecycleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAgentRequest {
  code?: string;
  name: string;
  description?: string | null;
  domain?: string | null;
  industryTags?: string[];
  isPlatform?: boolean;
  purpose?: string | null;
  scopeHonesty?: string | null;
  expertPosture?: string | null;
  visibility?: StudioAgentVisibility;
  createInitialVersion?: boolean;
}

export interface CreateAgentResult {
  agent: AiStudioAgent;
  version: AiStudioAgentVersion | null;
}

export interface CreatePackRequest {
  code?: string;
  name: string;
  domain?: string | null;
  isPlatform?: boolean;
}

/** Fields the pack editor may change while a pack is still a draft. */
export interface UpdatePackRequest {
  name?: string;
  domain?: string | null;
  description?: string | null;
  purpose?: string | null;
  scopeHonesty?: string | null;
  expertPosture?: string | null;
  vocabularyMap?: Record<string, unknown>;
  enabledPlugins?: unknown[];
  defaultWeights?: Record<string, unknown>;
  importTemplate?: Record<string, unknown>;
  appendInputExpected?: ExpectedFieldRow[];
  appendOutputExpected?: ExpectedFieldRow[];
  appendContext?: string | null;
  appendInstructions?: string | null;
  resourceTypes?: ResourceTypeSeed[];
}

/** Binding operational status (distinct from agent/pack lifecycle). */
export type StudioBindingStatus = 'active' | 'inactive' | 'archived';

export const STUDIO_BINDING_STATUSES: readonly StudioBindingStatus[] = [
  'active',
  'inactive',
  'archived',
];

export interface AiStudioAgentBinding {
  id: string;
  tenantId: string;
  agentVersionId: string;
  industryPackId: string | null;
  bindingName: string;
  customerSetup: Record<string, unknown>;
  autonomyFencesOverride: Record<string, unknown>;
  toolConnections: Record<string, unknown>;
  memoryScope: Record<string, unknown>;
  status: StudioBindingStatus;
  createdAt: string;
  updatedAt: string;
  /** Present on catalog/get responses (joined). */
  agentCode?: string;
  agentName?: string;
  versionNumber?: number;
  packCode?: string | null;
  packName?: string | null;
  /** Bound version execution mode (joined; Design §19.4 chip). */
  executionMode?: StudioExecutionMode;
  /** Bound version platform fences (joined; Design §19.4 summary). */
  versionAutonomyFences?: Record<string, unknown>;
}

export interface CreateBindingRequest {
  agentVersionId: string;
  bindingName: string;
  industryPackId?: string | null;
  status?: StudioBindingStatus;
}

export interface UpdateBindingRequest {
  bindingName?: string;
  industryPackId?: string | null;
  customerSetup?: Record<string, unknown>;
  autonomyFencesOverride?: Record<string, unknown>;
  toolConnections?: Record<string, unknown>;
  memoryScope?: Record<string, unknown>;
  status?: StudioBindingStatus;
}

/** Tenant agent instance (product) — backed by deploy row until dedicated table. */
export interface AiStudioAgentInstance {
  id: string;
  tenantId: string;
  pinnedVersionId: string;
  industryPackId: string | null;
  instanceName: string;
  instanceSetup: Record<string, unknown>;
  autonomyFencesOverride: Record<string, unknown>;
  toolConnections: Record<string, unknown>;
  memoryScope: Record<string, unknown>;
  status: StudioBindingStatus;
  createdAt: string;
  updatedAt: string;
  agentId?: string;
  agentCode?: string;
  agentName?: string;
  versionNumber?: number;
  packCode?: string | null;
  packName?: string | null;
  executionMode?: StudioExecutionMode;
  versionAutonomyFences?: Record<string, unknown>;
  businessHierarchyId?: string | null;
  functionalHierarchyId?: string | null;
  geographicHierarchyId?: string | null;
  setupConfigured?: boolean;
  setupStatus?: string;
  requiresSetup?: boolean;
}

export interface CreateInstanceRequest {
  pinnedVersionId: string;
  instanceName: string;
  industryPackId?: string | null;
  status?: StudioBindingStatus;
  businessHierarchyId?: string | null;
  functionalHierarchyId?: string | null;
  geographicHierarchyId?: string | null;
}

export interface InstanceDeployPatch {
  instanceName?: string;
  industryPackId?: string | null;
  businessHierarchyId?: string | null;
  functionalHierarchyId?: string | null;
  geographicHierarchyId?: string | null;
}

export interface InstanceProvisionPatch {
  deploy?: InstanceDeployPatch;
  setup?: {
    site?: { name?: string; location?: string };
    setup_notes?: string;
    fields?: Record<string, unknown>;
  };
  markSetupComplete?: boolean;
}

export interface PackAuthoringPatch {
  name?: string;
  domain?: string;
  description?: string;
  purpose?: string;
  scopeHonesty?: string;
  expertPosture?: string;
  appendContext?: string;
  appendInstructions?: string;
  appendInputExpected?: ExpectedFieldRow[];
  appendOutputExpected?: ExpectedFieldRow[];
  resourceTypes?: ResourceTypeSeed[];
}

export interface UpdateInstanceRequest {
  instanceName?: string;
  industryPackId?: string | null;
  instanceSetup?: Record<string, unknown>;
  autonomyFencesOverride?: Record<string, unknown>;
  toolConnections?: Record<string, unknown>;
  memoryScope?: Record<string, unknown>;
  status?: StudioBindingStatus;
  businessHierarchyId?: string | null;
  functionalHierarchyId?: string | null;
  geographicHierarchyId?: string | null;
}

/** Runtime status for an AI Studio run (WF-F4 / S6). */
export type StudioRunStatus =
  | 'open'
  | 'in_progress'
  | 'paused'
  | 'completed'
  | 'archived'
  | 'cancelled';

export const STUDIO_RUN_STATUSES: readonly StudioRunStatus[] = [
  'open',
  'in_progress',
  'paused',
  'completed',
  'archived',
  'cancelled',
];

export interface AiStudioRun {
  id: string;
  tenantId: string;
  agentVersionId: string;
  bindingId: string | null;
  ownerUserId: string | null;
  runName: string;
  situationType: string | null;
  effectiveTenantId: string | null;
  status: StudioRunStatus;
  scopeContext: Record<string, unknown>;
  currentThesis: Record<string, unknown>;
  /** Canonical working set for native compute (resources/demands/tasks). */
  workingSet: Record<string, unknown>;
  /** Last deterministic compute result, if any. */
  computeResult: Record<string, unknown> | null;
  /** Trial against a candidate version; production pin unchanged (REQ-AIS-015). */
  isTrial: boolean;
  createdAt: string;
  updatedAt: string;
  /** Present on S6 list/get responses (joined). */
  bindingName?: string;
  agentId?: string;
  agentCode?: string;
  agentName?: string;
  /** Persona fields from agent join (UX3). */
  agentPurpose?: string | null;
  agentExpertPosture?: string | null;
  versionNumber?: number;
  executionMode?: StudioExecutionMode;
  ownerName?: string | null;
  ownerEmail?: string | null;
}

export interface CreateRunRequest {
  /** Preferred UX1: start from agent (silent pin). */
  agentId?: string;
  /** Run within a deployed agent instance. */
  instanceId?: string;
  /** Legacy internal pin id — still accepted by API. */
  bindingId?: string;
  runName: string;
  situationType?: string | null;
  scopeContext?: Record<string, unknown>;
  /** Optional published candidate for a trial run (pin unchanged). */
  trialAgentVersionId?: string | null;
  /** Required when starting a run on an archived agent. */
  acknowledgeArchived?: boolean;
}

export interface RunFromAgentResponse {
  run: AiStudioRun;
  readiness: { ready: boolean; messages: string[] };
  agentArchived: boolean;
}

export interface RunReadinessResponse {
  readiness: { ready: boolean; messages: string[] };
  agentArchived: boolean;
  agentName: string | null;
}

/** Decision Case status values (schema CHECK on ai_studio_decision_cases). */
export type StudioDecisionCaseStatus =
  | 'open'
  | 'in_review'
  | 'resolved'
  | 'cancelled';

export interface AiStudioDecisionCase {
  id: string;
  tenantId: string;
  runId: string | null;
  conversationId: string | null;
  question: string;
  status: StudioDecisionCaseStatus;
  options: unknown[];
  assumptions: Record<string, unknown>;
  orchestrationMode: string;
  openedAt: string;
  closedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDecisionCaseRequest {
  question: string;
  runId?: string | null;
  conversationId?: string | null;
  options?: unknown[];
  assumptions?: Record<string, unknown>;
}

export interface UpdateDecisionCaseRequest {
  question?: string;
  options?: unknown[];
  assumptions?: Record<string, unknown>;
}

/** Human WF-F5 actions (mapped server-side onto schema statuses). */
export type StudioDecisionAction = 'approve' | 'reject' | 'defer';

export interface AiStudioDecisionContribution {
  id: string;
  decisionCaseId: string;
  agentBindingId: string | null;
  contributorRole: string;
  factsUsed: Record<string, unknown>;
  optionsOrConstraints: Record<string, unknown>;
  scoreImpact: Record<string, unknown>;
  confidence: number | null;
  provenance: Record<string, unknown>;
  createdAt: string;
}

export interface CreateDecisionContributionRequest {
  factsUsed?: Record<string, unknown>;
  optionsOrConstraints?: Record<string, unknown>;
  scoreImpact?: Record<string, unknown>;
  confidence?: number | null;
  provenance?: Record<string, unknown>;
  contributorRole?: string;
  agentBindingId?: string | null;
}

/** Fields the blueprint editor may change while a version is still a draft. */
export interface UpdateVersionRequest {
  enabledStages?: StudioSpineStage[];
  dataSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  benchmarkConfig?: Record<string, unknown>;
  opportunityLogic?: Record<string, unknown>;
  engineConfig?: Record<string, unknown>;
  promotionRules?: Record<string, unknown>;
  executionMode?: StudioExecutionMode;
  autonomyFences?: Record<string, unknown>;
  defaultDecisionMode?: StudioDecisionMode;
  contextContract?: Record<string, unknown>;
  computeClasses?: StudioComputeClass[];
  archetype?: string | null;
  industryPackId?: string | null;
  inputExpected?: ExpectedFieldRow[];
  outputExpected?: ExpectedFieldRow[];
  contextDefinition?: string | null;
  explainInstructions?: string | null;
  successCriteria?: string | null;
  hitlRequired?: boolean;
}

/** Platform ontology concept kinds (ai_ontology_concepts.concept_kind). */
export type OntologyConceptKind =
  | 'entity'
  | 'event'
  | 'attribute'
  | 'relationship'
  | 'constraint'
  | 'other';

export type OntologyLifecycleStatus = 'draft' | 'active' | 'deprecated' | 'archived';

export const ONTOLOGY_CONCEPT_KINDS: readonly OntologyConceptKind[] = [
  'entity',
  'event',
  'attribute',
  'relationship',
  'constraint',
  'other',
];

export interface AiOntologyConcept {
  id: string;
  tenantId: string | null;
  code: string;
  name: string;
  description: string | null;
  conceptKind: OntologyConceptKind;
  version: number;
  isPlatform: boolean;
  lifecycleStatus: OntologyLifecycleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AiOntologyRelationship {
  id: string;
  tenantId: string | null;
  code: string;
  name: string;
  description: string | null;
  fromConceptId: string;
  toConceptId: string;
  cardinality: string;
  isPlatform: boolean;
  lifecycleStatus: OntologyLifecycleStatus;
  createdAt: string;
  updatedAt: string;
}

export interface AiOntologyAttribute {
  id: string;
  conceptId: string;
  code: string;
  name: string;
  dataType: string;
  isRequired: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A named compute slot declared on a version (camelCase view model).
 * Replaces the free-form engineConfig JSONB for structured compute wiring.
 */
export interface AiStudioVersionComputeSlot {
  id: string;
  tenantId: string;
  agentVersionId: string;
  slotName: string;
  computeClass: StudioComputeClass;
  /** Algorithm (deterministic), platform model id (learned), or model config key (generative). Null = resolved at binding. */
  defaultRef: string | null;
  parameters: Record<string, unknown>;
  fallbackPolicy: StudioSlotFallbackPolicy;
  isRequired: boolean;
  sortOrder: number;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateComputeSlotRequest {
  slotName: string;
  computeClass: StudioComputeClass;
  defaultRef?: string | null;
  parameters?: Record<string, unknown>;
  fallbackPolicy?: StudioSlotFallbackPolicy;
  isRequired?: boolean;
  sortOrder?: number;
  description?: string | null;
}

export interface UpdateComputeSlotRequest {
  slotName?: string;
  computeClass?: StudioComputeClass;
  defaultRef?: string | null;
  parameters?: Record<string, unknown>;
  fallbackPolicy?: StudioSlotFallbackPolicy;
  isRequired?: boolean;
  sortOrder?: number;
  description?: string | null;
}

/** Decision-agent tool kinds (REQ-AIS-039). */
export type StudioToolKind = 'read' | 'compute' | 'write';

export const STUDIO_TOOL_KINDS: readonly StudioToolKind[] = ['read', 'compute', 'write'];

export interface AiStudioVersionTool {
  id: string;
  tenantId: string | null;
  agentVersionId: string;
  toolName: string;
  toolKind: StudioToolKind;
  handlerRef: string;
  description: string;
  parametersSchema: Record<string, unknown>;
  isEnabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVersionToolRequest {
  toolName: string;
  toolKind: StudioToolKind;
  handlerRef: string;
  description: string;
  parametersSchema?: Record<string, unknown>;
  isEnabled?: boolean;
  sortOrder?: number;
}

export interface UpdateVersionToolRequest {
  toolName?: string;
  toolKind?: StudioToolKind;
  handlerRef?: string;
  description?: string;
  parametersSchema?: Record<string, unknown>;
  isEnabled?: boolean;
  sortOrder?: number;
}

/** Tenant-level override that resolves a version compute slot to a customer model endpoint. */
export interface AiStudioBindingSlotOverride {
  id: string;
  tenantId: string;
  bindingId: string;
  slotId: string;
  overrideRef: string;
  parameters: Record<string, unknown>;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBindingSlotOverrideRequest {
  slotId: string;
  overrideRef: string;
  parameters?: Record<string, unknown>;
  notes?: string | null;
}

/** AI authoring interview — suggested agent identity patch. */
export interface AuthoringAgentPatch {
  name?: string;
  domain?: string;
  purpose?: string;
  scopeHonesty?: string;
  expertPosture?: string;
}

/** AI authoring interview — suggested blueprint patch. */
export interface AuthoringVersionPatch {
  contextDefinition?: string;
  successCriteria?: string;
  explainInstructions?: string;
  executionMode?: StudioExecutionMode;
  defaultDecisionMode?: StudioDecisionMode;
  computeClasses?: StudioComputeClass[];
  archetype?: string | null;
  inputExpected?: ExpectedFieldRow[];
  outputExpected?: ExpectedFieldRow[];
}

export interface DefinitionProgressItem {
  id: string;
  complete: boolean;
}

export interface DefinitionProgress {
  readyForTest: boolean;
  readyToPublish: boolean;
  items: DefinitionProgressItem[];
  publishMessages: string[];
}

export interface AuthoringMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  suggestedAgentPatch?: AuthoringAgentPatch;
  suggestedVersionPatch?: AuthoringVersionPatch;
}

export interface AuthoringSession {
  conversationId: string;
  versionId: string;
  messages: AuthoringMessage[];
  progress: DefinitionProgress;
}

export interface AuthoringSendResult {
  userMessage: AuthoringMessage;
  assistantMessage: AuthoringMessage;
  progress: DefinitionProgress;
  tokensUsed: number;
}
